// src/components/roads/drive/driveAiApi.js
// --- UPDATE (Drive Mode AI review) -----------------------------------------------------
// API + shared constants for the Drive Mode AI panels (no React).
//   GET  /roads/drive/overview?road_id=&verdict=&damage_type=&segment_index=&limit=
//   POST /roads/drive/findings/:id/review          { verdict: confirm|reject|correct, label }
//   POST /roads/:id/segments/drive-health          confirmed damage → segment PCI (ML / rules)
//   GET  /vision/stats · POST /vision/train        road-damage detection model
// Photos are public files under /uploads (served by server.js) — photoSrc() builds the URL.
// --- END UPDATE ------------------------------------------------------------------------
import { apiFetch, API_BASE } from '../page/shared';

export const DAMAGE = Object.freeze({
    pothole: { label: 'Pothole', color: '#E53E3E', scheme: 'red' },
    alligator_cracking: { label: 'Alligator cracking', color: '#DD6B20', scheme: 'orange' },
    longitudinal_cracking: { label: 'Longitudinal cracking', color: '#D69E2E', scheme: 'yellow' },
    transverse_cracking: { label: 'Transverse cracking', color: '#B7791F', scheme: 'yellow' },
    edge_cracking: { label: 'Edge cracking', color: '#805AD5', scheme: 'purple' },
    rutting: { label: 'Rutting', color: '#C05621', scheme: 'orange' },
    patching: { label: 'Patching', color: '#38A169', scheme: 'green' },
    other_damage: { label: 'Other damage', color: '#718096', scheme: 'gray' },
});
export const DAMAGE_KEYS = Object.keys(DAMAGE).filter((k) => k !== 'other_damage');
export const damageInfo = (k) => DAMAGE[k] || DAMAGE.other_damage;

export const VERDICT = Object.freeze({
    reported: { label: 'Needs review', scheme: 'yellow' },
    confirmed: { label: 'Confirmed', scheme: 'green' },
    corrected: { label: 'Corrected', scheme: 'blue' },
    rejected: { label: 'Not damage', scheme: 'gray' },
});
export const verdictInfo = (v) => VERDICT[v] || { label: v || '—', scheme: 'gray' };

const ORIGIN = String(API_BASE || '').replace(/\/api(\/v\d+)?\/?$/, '');
/** Absolute URL of a stored photo (/uploads/... or already absolute). */
export const photoSrc = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const qs = (o) => Object.entries(o).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

export const getDriveOverview = ({ roadId, verdict, damageType, segmentIndex, limit = 60 } = {}) =>
    apiFetch(`/roads/drive/overview?${qs({ road_id: roadId, verdict, damage_type: damageType, segment_index: segmentIndex, limit })}`).then((r) => r?.data || null);

export const reviewDriveFinding = (id, verdict, label) =>
    apiFetch(`/roads/drive/findings/${encodeURIComponent(id)}/review`, { method: 'POST', body: JSON.stringify({ verdict, label }) });

export const refreshDriveSegmentHealth = (roadId) =>
    apiFetch(`/roads/${encodeURIComponent(roadId)}/segments/drive-health`, { method: 'POST', body: '{}' }).then((r) => r?.data || null);

// --- UPDATE (startup fix): served by the roads router — /api/v1/vision is not mounted on every server
export const getVisionStats = () => apiFetch('/roads/vision/stats').then((r) => r?.data || null);
export const startVisionTraining = () => apiFetch('/roads/vision/train', { method: 'POST', body: '{}' }).then((r) => r?.data || null);
// --- END UPDATE

export const pct = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : `${Math.round(Number(v) * 100)}%`);
export const fmtWhen = (d) => {
    if (!d) return '—';
    const t = new Date(d);
    return Number.isFinite(t.getTime()) ? t.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
};
export const fmtM = (m) => (m === null || m === undefined || !Number.isFinite(Number(m)) ? '—' : Number(m) >= 1000 ? `${(Number(m) / 1000).toFixed(2)} km` : `${Math.round(Number(m))} m`);
export const pciScheme = (v) => (v === null || v === undefined ? 'gray' : v < 40 ? 'red' : v < 55 ? 'orange' : v < 70 ? 'yellow' : v < 85 ? 'teal' : 'green');
export const pciLabel = (v) => (v === null || v === undefined ? 'No score' : v < 40 ? 'Critical' : v < 55 ? 'Poor' : v < 70 ? 'Fair' : v < 85 ? 'Good' : 'Excellent');
